import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  getDataPrkgService,
  getHistoryService,
  getBikesService,
  createCycleService,
  closeCycleService,
  createUserService,
  createBikeService,
  getUsersService,
  enrichHistory,
} from "modules/came/services/bicicletasService";

import { hasRecordId } from "../utils/recordIds";
import resolveParkingBike from "../model/resolveParkingBike";
import { bikeDateTimeForApi } from "modules/came/model/bikeDateTime";

const TOTAL_GANCHOS = 30;

// ============ HELPERS DE NORMALIZACIÓN ============
// Toma el primer valor no vacío entre varias claves posibles
const pick = (obj, ...keys) => {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return null;
};

// Construye el objeto "usuario" (con datos de user + bike) para un gancho ocupado
const buildGanchoUsuario = (rawItem, usersMap, bikesMap) => {
  if (!rawItem) return null;

  const user = usersMap.get(String(rawItem.id_user)) || null;
  const { bike, bikeAssociationNeedsReview } = resolveParkingBike(rawItem, bikesMap);

  // Si no hay ni user ni bike, no hay nada que mostrar
  if (!user && !bike) return null;

  const nombre = pick(user, "names", "nombre", "name") || "";
  const apellido = pick(user, "lastnames", "apellido", "lastname") || "";
  const nombreCompleto = `${nombre} ${apellido}`.trim();

  return {
    // Usuario
    nombre: nombreCompleto || null,
    cedula: pick(user, "identification", "cedula", "document", "documento") || null,
    celular: pick(user, "more", "phone", "celular", "telefono", "mobile") || null,

    // Bicicleta
    marca: pick(bike, "brand", "marca") || null,
    color: pick(bike, "color") || null,
    serie: pick(bike, "code", "serie", "serial") || null,
    bikeAssociationNeedsReview,
    referencedBikeId: rawItem.id_byke,
    displayedBikeId: bike?.id,

    // Ciclo
    entrada: pick(rawItem, "enter_at", "entrada", "entry_date") || null,
    cicloId: rawItem.id || null,
  };
};

// Construye los 30 ganchos: los ocupados con datos cruzados, los libres vacíos
const buildGanchos = (ganchosRaw, users, bikes) => {
  const usersMap = new Map((users || []).map((u) => [String(u.id), u]));
  const bikesMap = new Map((bikes || []).map((b) => [String(b.id), b]));

  // Indexar los ciclos activos (sin out_at) por número de gancho
  const ocupadosPorGancho = new Map();
  (ganchosRaw || []).forEach((item) => {
    if (!item) return;
    if (item.out_at) return; // ya salió
    const nro = Number(item.harness);
    if (!Number.isFinite(nro)) return;
    ocupadosPorGancho.set(nro, item);
  });

  return Array.from({ length: TOTAL_GANCHOS }, (_, i) => {
    const id = i + 1;
    const raw = ocupadosPorGancho.get(id);

    if (!raw) {
      return { id, disponible: true, usuario: null, cicloId: null };
    }

    return {
      id,
      disponible: false,
      usuario: buildGanchoUsuario(raw, usersMap, bikesMap),
      cicloId: raw.id || null,
    };
  });
};

export const useFormBicicleta = (onIngreso, onSalida, refreshFlag) => {
  const [ganchos, setGanchos] = useState(() =>
    Array.from({ length: TOTAL_GANCHOS }, (_, i) => ({
      id: i + 1,
      disponible: true,
      usuario: null,
      cicloId: null,
    }))
  );
  const [ganchosRaw, setGanchosRaw] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [users, setUsers] = useState([]);
  const [bikes, setBikes] = useState([]);

  const [loadingGanchos, setLoadingGanchos] = useState(false);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingBikes, setLoadingBikes] = useState(false);

  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const isMounted = useRef(true);
  const hasLoadedRef = useRef(false);

  const usersRef = useRef([]);
  const bikesRef = useRef([]);

  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  useEffect(() => {
    bikesRef.current = bikes;
  }, [bikes]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ========== CARGAR GANCHOS ==========
  const loadGanchos = useCallback(async () => {
    setLoadingGanchos(true);
    setError(null);
    try {
      const result = await getDataPrkgService();
      if (!isMounted.current) return;

      if (result?.success) {
        const raw = Array.isArray(result.data) ? result.data : [];
        setGanchosRaw(raw);

        // Construir ganchos con los datos actuales en refs
        const built = buildGanchos(raw, usersRef.current || [], bikesRef.current || []);
        setGanchos(built);
      } else {
        setError(result?.error || "Error al cargar ganchos");
      }
    } catch (err) {
      if (isMounted.current) setError(err.message || "Error al cargar ganchos");
    } finally {
      if (isMounted.current) setLoadingGanchos(false);
    }
  }, []);

  // ========== CARGAR USUARIOS ==========
  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const result = await getUsersService();
      if (!isMounted.current) return [];

      if (result?.success) {
        const list = Array.isArray(result.data) ? result.data : [];
        setUsers(list);
        usersRef.current = list;
        return list;
      }
      return [];
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
      return [];
    } finally {
      if (isMounted.current) setLoadingUsers(false);
    }
  }, []);

  // ========== CARGAR BICICLETAS ==========
  const loadBikes = useCallback(async () => {
    setLoadingBikes(true);
    try {
      const result = await getBikesService();
      if (!isMounted.current) return [];

      if (result?.success) {
        const list = Array.isArray(result.data) ? result.data : [];
        setBikes(list);
        bikesRef.current = list;
        return list;
      }
      return [];
    } catch (err) {
      console.error("Error al cargar bicicletas:", err);
      return [];
    } finally {
      if (isMounted.current) setLoadingBikes(false);
    }
  }, []);

  // ========== CARGAR HISTÓRICO ==========
  const loadHistorico = useCallback(async (params = {}) => {
    setLoadingHistorico(true);
    try {
      const result = await getHistoryService(params);
      if (!isMounted.current) return;

      if (result?.success) {
        const raw = Array.isArray(result.data) ? result.data : [];
        const enriched = enrichHistory(raw, usersRef.current || [], bikesRef.current || []);
        setHistorico(Array.isArray(enriched) ? enriched : []);
      } else {
        setHistorico([]);
      }
    } catch (err) {
      console.error("❌ [loadHistorico] exception:", err);
      if (isMounted.current) setHistorico([]);
    } finally {
      if (isMounted.current) setLoadingHistorico(false);
    }
  }, []);

  // ========== CARGA INICIAL ==========
  useEffect(() => {
    if (hasLoadedRef.current || !isMounted.current) return;
    hasLoadedRef.current = true;

    (async () => {
      await Promise.all([loadUsers(), loadBikes()]);
      await Promise.all([loadGanchos(), loadHistorico()]);
    })();
  }, [loadUsers, loadBikes, loadGanchos, loadHistorico]);

  // ========== REFRESH ==========
  useEffect(() => {
    if (!refreshFlag || !hasLoadedRef.current) return;

    (async () => {
      await Promise.all([loadUsers(), loadBikes()]);
      await Promise.all([loadGanchos(), loadHistorico()]);
    })();
  }, [refreshFlag, loadUsers, loadBikes, loadGanchos, loadHistorico]);

  // ✅ RE-CONSTRUIR GANCHOS cuando lleguen users o bikes
  useEffect(() => {
    if (!ganchosRaw || ganchosRaw.length === 0) return;
    if (!users || users.length === 0) return;
    if (!bikes || bikes.length === 0) return;

    const built = buildGanchos(ganchosRaw, users, bikes);
    setGanchos(built);
  }, [ganchosRaw, users, bikes]);

  // ========== GUARDAR USUARIO ==========
  const guardarUsuario = useCallback(
    async (formData) => {
      try {
        const existing = (usersRef.current || []).find(
          (u) => String(u.identification).trim() === String(formData.cedula).trim()
        );

        if (existing && hasRecordId(existing.id)) {
          return {
            success: true,
            data: {
              id: existing.id,
              identification: existing.identification,
              names: existing.names,
              lastnames: existing.lastnames,
              id_type: existing.id_type,
              phone: existing.more || "",
            },
            message: "Usuario existente recuperado",
          };
        }

        const result = await createUserService({
          identification: formData.cedula,
          id_type: Number(formData.idType) || 1,
          names: formData.nombre,
          lastnames: formData.apellido,
          phone: formData.celular,
        });

        if (!result?.success) throw new Error(result?.error || "Error al crear usuario");

        const refreshedUsers = await loadUsers();
        const persistedUser = refreshedUsers.find(
          (item) =>
            String(item.identification).trim() === String(formData.cedula).trim() &&
            hasRecordId(item.id)
        );
        const newId = persistedUser?.id;
        if (!hasRecordId(newId)) {
          throw new Error(
            "El usuario se guardó, pero no se pudo recuperar su ID. Vuelve a buscarlo por documento antes de continuar."
          );
        }
        const newUser = {
          id: newId,
          identification: formData.cedula,
          names: formData.nombre,
          lastnames: formData.apellido,
          id_type: formData.idType,
          phone: formData.celular,
        };

        return { success: true, data: newUser };
      } catch (err) {
        console.error("❌ Error guardarUsuario:", err);
        return { success: false, error: err.message };
      }
    },
    [loadUsers]
  );

  // ========== GUARDAR BICICLETA ==========
  const guardarBicicleta = useCallback(
    async (bikePayload, savedUser) => {
      try {
        if (!hasRecordId(savedUser?.id))
          throw new Error(
            "No se recibió el ID del usuario. Vuelve al paso Usuario y selecciónalo por documento."
          );

        const payload = {
          code: bikePayload.code || bikePayload.serie,
          color: bikePayload.color,
          id_user: savedUser.id,
        };

        if (bikePayload.marca) {
          payload.brand = bikePayload.marca;
        }

        const result = await createBikeService(payload);

        if (!result?.success) throw new Error(result?.error || "Error al crear bicicleta");

        const refreshedBikes = await loadBikes();
        const matchingBikes = refreshedBikes.filter(
          (item) =>
            String(item.id_user) === String(savedUser.id) &&
            String(item.code || item.serie) === String(payload.code) &&
            String(item.color).trim().toLowerCase() ===
              String(payload.color).trim().toLowerCase() &&
            hasRecordId(item.id)
        );
        const persistedBike = matchingBikes.length === 1 ? matchingBikes[0] : null;
        const bikeId = persistedBike?.id;
        if (!hasRecordId(bikeId))
          throw new Error(
            "La bicicleta se guardó, pero no se pudo recuperar su ID. Selecciónala de la lista antes de continuar."
          );

        return {
          success: true,
          data: {
            id: bikeId,
            code: persistedBike.code || persistedBike.serie,
            color: persistedBike.color,
            id_user: savedUser.id,
            brand: bikePayload.marca || "",
          },
        };
      } catch (err) {
        console.error("❌ Error guardarBicicleta:", err);
        return { success: false, error: err.message };
      }
    },
    [loadBikes]
  );

  // ========== REGISTRAR CICLO ==========
  const registrarCiclo = useCallback(
    async ({ userId, bikeId, ganchoId, modo, cicloId, ...rest }) => {
      setSubmitError(null);
      setSubmitSuccess(false);

      try {
        let result;
        let confirmedBikeId = bikeId;

        if (modo === "INGRESO") {
          if (!userId) throw new Error("Usuario no guardado");
          if (!bikeId) throw new Error("Bicicleta no guardada");

          const currentBikes = await loadBikes();
          const selectedBike = currentBikes.find((item) => String(item.id) === String(bikeId));
          const requestedCode = String(rest.serie || rest.code || "").trim();
          const matchingBikes = requestedCode
            ? currentBikes.filter(
                (item) =>
                  String(item.id_user) === String(userId) &&
                  String(item.code || item.serie || "").trim() === requestedCode
              )
            : [];
          const verifiedBike =
            selectedBike &&
            String(selectedBike.id_user) === String(userId) &&
            (!requestedCode ||
              String(selectedBike.code || selectedBike.serie || "").trim() === requestedCode)
              ? selectedBike
              : matchingBikes.length === 1
              ? matchingBikes[0]
              : null;
          if (!verifiedBike || !hasRecordId(verifiedBike.id)) {
            throw new Error(
              "La bicicleta seleccionada no está asociada a este usuario. Vuelve al paso Bicicleta y selecciona la correcta."
            );
          }
          confirmedBikeId = verifiedBike.id;

          result = await createCycleService({
            id_user: userId,
            id_byke: confirmedBikeId,
            harness: ganchoId || null,
            enter_at: bikeDateTimeForApi(),
          });
        } else {
          if (!cicloId) throw new Error("No se recibió el ID del ciclo");
          result = await closeCycleService({
            id: cicloId,
            out_at: bikeDateTimeForApi(),
          });
        }

        if (!result?.success) throw new Error(result?.error || "Error al registrar");

        setSubmitSuccess(true);

        if (onIngreso && modo === "INGRESO") {
          onIngreso({ ...rest, userId, bikeId: confirmedBikeId, ganchoId, modo: "INGRESO" });
        }
        if (onSalida && modo === "SALIDA") {
          onSalida({ ...rest, userId, bikeId, ganchoId, cicloId, modo: "SALIDA" });
        }

        await Promise.all([loadUsers(), loadBikes()]);
        await Promise.all([loadGanchos(), loadHistorico()]);

        return { success: true, data: result.data };
      } catch (err) {
        console.error("❌ Error registrarCiclo:", err);
        setSubmitError(err.message);
        return { success: false, error: err.message };
      }
    },
    [onIngreso, onSalida, loadUsers, loadBikes, loadGanchos, loadHistorico]
  );

  // ========== UTILIDADES ==========
  const totalDisponibles = useMemo(
    () => (Array.isArray(ganchos) ? ganchos.filter((g) => g.disponible).length : 0),
    [ganchos]
  );
  const totalOcupados = useMemo(
    () => (Array.isArray(ganchos) ? ganchos.filter((g) => !g.disponible).length : 0),
    [ganchos]
  );

  // ========== PARQUEADERO ACTUAL ==========
  const parqueaderoActual = useMemo(() => {
    if (!Array.isArray(ganchosRaw)) return [];

    const usersMap = new Map((users || []).map((u) => [String(u.id), u]));
    const bikesMap = new Map((bikes || []).map((b) => [String(b.id), b]));

    return ganchosRaw
      .filter((item) => item && !item.out_at)
      .map((item) => {
        const user = usersMap.get(String(item.id_user)) || null;
        const { bike, bikeAssociationNeedsReview } = resolveParkingBike(item, bikesMap);

        return {
          id: item.id,
          id_byke: item.id_byke,
          id_user: item.id_user,
          enter_at: item.enter_at,
          harness: item.harness,
          status: item.status,

          nombre: user ? `${user.names || ""} ${user.lastnames || ""}`.trim() : "",
          cedula: user?.identification || "",
          celular: user?.more || user?.phone || "",

          code: bike?.code || bike?.serie || "",
          color: bike?.color || "",
          marca: bike?.brand || bike?.marca || "",
          bikeAssociationNeedsReview,
        };
      });
  }, [ganchosRaw, users, bikes]);

  const resetSubmitState = useCallback(() => {
    setSubmitError(null);
    setSubmitSuccess(false);
  }, []);

  const reloadAll = useCallback(async () => {
    hasLoadedRef.current = false;
    await Promise.all([loadUsers(), loadBikes()]);
    await Promise.all([loadGanchos(), loadHistorico()]);
    hasLoadedRef.current = true;
  }, [loadGanchos, loadHistorico, loadUsers, loadBikes]);

  return {
    ganchos: Array.isArray(ganchos) ? ganchos : [],
    ganchosRaw: Array.isArray(ganchosRaw) ? ganchosRaw : [],
    historico: Array.isArray(historico) ? historico : [],
    users: Array.isArray(users) ? users : [],
    bikes: Array.isArray(bikes) ? bikes : [],
    parqueaderoActual: Array.isArray(parqueaderoActual) ? parqueaderoActual : [],
    totalDisponibles,
    totalOcupados,

    loadingGanchos,
    loadingHistorico,
    loadingUsers,
    loadingBikes,

    error,
    submitError,
    submitSuccess,

    guardarUsuario,
    guardarBicicleta,
    registrarCiclo,

    loadGanchos,
    loadHistorico,
    loadUsers,
    loadBikes,
    reloadAll,
    resetSubmitState,
  };
};

export default useFormBicicleta;
